import React, { useState, useEffect } from 'react';
import { CheckCircle, TrendingUp, DollarSign, BarChart3, Users, Shield, Settings, ArrowRight, Cloud, Zap } from 'lucide-react';

interface ContentSection {
  title: string;
  type?: 'cards' | 'list' | 'rich-text' | 'benefits' | 'process' | 'tools';
  content?: Array<{
    title: string;
    description: string;
    features?: string[];
    icon?: string;
    metrics?: string[];
  }>;
  richContent?: string;
  items?: string[];
  processSteps?: Array<{
    step: number;
    title: string;
    description: string;
  }>;
  tools?: Array<{
    category: string;
    items: string[];
  }>;
}

interface ServiceContentProps {
  sections: ContentSection[];
}

const ServiceContent: React.FC<ServiceContentProps> = ({ sections }) => {
  const [countUp, setCountUp] = useState({ savings: 0, visibility: 0, efficiency: 0 });
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setCountUp({ savings: 40, visibility: 95, efficiency: 60 });
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const renderCards = (content: any[]) => (
    <div className="modern-cards-grid">
      {content.map((item, index) => (
        <div key={index} className="modern-service-card">
          <div className="card-header">
            <div className="card-icon-placeholder">
              <span className="card-number">{String(index + 1).padStart(2, '0')}</span>
            </div>
            <h3 className="card-title">
              {item.icon && <span className="card-emoji">{item.icon}</span>}
              {item.title}
            </h3>
          </div>
          
          <div className="card-body">
            <p className="card-description">{item.description}</p>
            
            {item.features && (
              <div className="features-container">
                <div className="features-header">
                  <span className="features-label">KEY FEATURES</span>
                </div>
                <ul className="modern-features-list">
                  {item.features.map((feature: string, featureIndex: number) => (
                    <li key={featureIndex} className="feature-item">
                      <span className="feature-checkmark">✓</span>
                      <span className="feature-text">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  const renderBenefits = (content: any[]) => (
    <div className="benefits-showcase">
      {content.map((item, index) => (
        <div key={index} className="benefit-card">
          <div className="benefit-icon-container">
            
            {item.icon && <span className="benefit-icon">{item.icon}</span>}
            <div className="icon-background"></div>
          </div>
          
          <div className="benefit-content">
            <h3 className="benefit-title">{item.title}</h3>
            <p className="benefit-description">{item.description}</p>
            
            {item.features && (
              <div className="benefit-features">
                {item.features.map((feature: string, featureIndex: number) => (
                  <div key={featureIndex} className="benefit-feature-tag">
                    {feature}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  const renderProcess = (processSteps: any[]) => (
    <div className="process-timeline">
      <div className="timeline-line-service-content"></div>
      {processSteps.map((step, index) => (
        <div key={index} className="timeline-step">
          <div className="step-indicator">
            <div className="step-number-service-content">{index + 1}</div>
          </div>
          
          <div className="step-content-service-content">
            <div className="step-badge">Step {index + 1}</div>
            <h3 className="step-title-service-content">{step.title}</h3>
            <p className="step-description-service-content">{step.description}</p>
          </div>
        </div>
      ))}
    </div>
  );

  const renderTools = (tools: any[]) => (
    <div className="tech-orbital">
      {tools.map((category, index) => (
        <div key={index} className={`tech-system ${index % 2 === 0 ? 'tech-system-even' : 'tech-system-odd'}`}>
          <div className="tech-system-header">
            <div className="tech-category-title">
              {category.category}
            </div>
            <div className="tech-title-underline"></div>
          </div>
          
          <div className="tech-system-content">
            <div className="tech-orbit-container">
              <div className="tech-core">
                <span className="tech-core-icon">
                  {category.icon || "🔧"}
                </span>
              </div>
              <div className="tech-orbit">
                {category.items.map((tool: string, toolIndex: number) => {
                  // Position tools evenly around the circle
                  const angle = (360 / category.items.length) * toolIndex;
                  
                  return (
                    <div 
                      key={toolIndex} 
                      className="tech-spoke"
                      style={{
                        transform: `rotate(${angle}deg)`
                      }}
                    >
                      <div className="tech-pill">
                        <div className="tech-tool-icon">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <span className="tech-tool-name">{tool}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="tech-category-summary">
              <h4 className="summary-title">{category.category}</h4>
              <p className="summary-description">
                {category.description || `Our ${category.category} solutions are designed to help businesses leverage the latest technologies and best practices to achieve their operational goals and drive digital transformation.`}
              </p>
              <div className="summary-highlights">
                <div className="highlight-item">
                  <span className="highlight-icon">✓</span>
                  <span className="highlight-text">Expert consultation and implementation</span>
                </div>
                <div className="highlight-item">
                  <span className="highlight-icon">✓</span>
                  <span className="highlight-text">Industry best practices</span>
                </div>
                <div className="highlight-item">
                  <span className="highlight-icon">✓</span>
                  <span className="highlight-text">Ongoing support and optimization</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderList = (items: string[]) => (
    <div className="elegant-list">
      {items.map((item, index) => (
        <div key={index} className="list-item-modern">
          <div className="item-indicator">
            <span className="item-number">{index + 1}</span>
          </div>
          <div className="item-content">
            <p className="item-text">{item}</p>
          </div>
        </div>
      ))}
    </div>
  );

  const renderRichText = () => {
  
    const features = [
      {
        icon: <BarChart3 className="w-4 h-4" />,
        text: "Real-time cost visibility across multi-cloud environments"
      },
      {
        icon: <Users className="w-4 h-4" />,
        text: "Team-level cost ownership and attribution"
      },
      {
        icon: <Shield className="w-4 h-4" />,
        text: "Automated governance and policy enforcement"
      },
      {
        icon: <Settings className="w-4 h-4" />,
        text: "Developer-integrated cost awareness tools"
      }
    ];
  
    const stats = [
      { value: countUp.savings, suffix: '%', label: 'Average Cost Reduction', color: 'text-green-600' },
      { value: countUp.visibility, suffix: '%', label: 'Cost Visibility Increase', color: 'text-blue-600' },
      { value: countUp.efficiency, suffix: '%', label: 'Resource Efficiency Gain', color: 'text-purple-600' }
    ];
  
    return (
      <div className="bg-gray-50 py-20 px-4 relative overflow-hidden">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-32 h-32 bg-blue-100 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-40 h-40 bg-green-100 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-36 h-36 bg-purple-100 rounded-full blur-3xl"></div>
        </div>
  
        <div className="max-w-6xl mx-auto relative z-10">
          {/* Header with visual emphasis */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-3 bg-white rounded-full px-6 py-3 shadow-lg border mb-8 hover:shadow-xl transition-shadow">
              <div className="w-10 h-10 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-sm">
                <DollarSign className="w-5 h-5 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700 bg-gray-900 text-white px-4 py-1.5 rounded-full">
                FINOPS
              </span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Struggling with Rising
              <br />
              <span className="relative">
                Cloud Costs?
                <svg className="absolute -bottom-2 left-0 w-full h-3" viewBox="0 0 300 12" fill="none">
                  <path d="M0 6C50 2, 150 2, 300 6" stroke="#10B981" strokeWidth="3" fill="none" className="animate-pulse"/>
                </svg>
              </span>
            </h1>
            
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8 leading-relaxed">
              We Help You <span className="font-semibold text-gray-900">Take Control</span>
            </p>
  
            {/* Enhanced Stats showcase */}
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
              {stats.map((stat, index) => (
                <div key={index} className="group relative">
                  {/* Animated background gradient */}
                  <div className={`absolute inset-0 rounded-3xl bg-gradient-to-br ${
                    index === 0 ? 'from-green-400/20 to-emerald-600/20' :
                    index === 1 ? 'from-blue-400/20 to-indigo-600/20' :
                    'from-purple-400/20 to-violet-600/20'
                  } opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl`}></div>
                  
                  <div className="relative bg-white rounded-3xl p-8 shadow-xl border-2 border-gray-100 hover:border-gray-200 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl">
                    {/* Icon and accent */}
                    <div className="flex items-center justify-between mb-6">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                        index === 0 ? 'bg-green-100 text-green-600' :
                        index === 1 ? 'bg-blue-100 text-blue-600' :
                        'bg-purple-100 text-purple-600'
                      } group-hover:scale-110 transition-transform duration-300`}>
                        {index === 0 && <TrendingUp className="w-6 h-6" />}
                        {index === 1 && <BarChart3 className="w-6 h-6" />}
                        {index === 2 && <Zap className="w-6 h-6" />}
                      </div>
                      
                      {/* Sparkle animation */}
                      <div className="relative">
                        <div className={`w-2 h-2 rounded-full ${
                          index === 0 ? 'bg-green-400' :
                          index === 1 ? 'bg-blue-400' :
                          'bg-purple-400'
                        } animate-pulse`}></div>
                        <div className={`absolute -top-1 -right-1 w-1 h-1 rounded-full ${
                          index === 0 ? 'bg-green-300' :
                          index === 1 ? 'bg-blue-300' :
                          'bg-purple-300'
                        } animate-ping`}></div>
                      </div>
                    </div>
                    
                    {/* Main stat number with enhanced styling */}
                    <div className="mb-4">
                      <div className="flex items-baseline gap-1">
                        <span className={`text-5xl font-bold bg-gradient-to-r ${
                          index === 0 ? 'from-green-600 to-emerald-600' :
                          index === 1 ? 'from-blue-600 to-indigo-600' :
                          'from-purple-600 to-violet-600'
                        } bg-clip-text text-transparent`}>
                          {stat.value}
                        </span>
                        <span className={`text-2xl font-semibold ${stat.color}`}>
                          {stat.suffix}
                        </span>
                      </div>
                      
                      {/* Progress bar */}
                      <div className="w-full bg-gray-100 rounded-full h-2 mt-3 overflow-hidden">
                        <div 
                          className={`h-2 rounded-full bg-gradient-to-r ${
                            index === 0 ? 'from-green-400 to-emerald-500' :
                            index === 1 ? 'from-blue-400 to-indigo-500' :
                            'from-purple-400 to-violet-500'
                          } transition-all duration-1000 ease-out`}
                          style={{ width: `${stat.value}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    {/* Label with enhanced typography */}
                    <div className="text-gray-600 font-medium text-sm leading-tight">
                      {stat.label}
                    </div>
                    
                    {/* Subtle bottom accent */}
                    <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${
                      index === 0 ? 'from-transparent via-green-400 to-transparent' :
                      index === 1 ? 'from-transparent via-blue-400 to-transparent' :
                      'from-transparent via-purple-400 to-transparent'
                    } opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-b-3xl`}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
  
          {/* Problem statement with visual hierarchy */}
          <div className="bg-white rounded-3xl shadow-xl border p-10 mb-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-red-50 to-orange-50 rounded-full transform translate-x-16 -translate-y-16"></div>
            
            <div className="relative z-10">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">The Cloud Cost Challenge</h2>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    If you're spending more on cloud infrastructure than you'd planned — and still don't have clear answers about 
                    what's driving that spend — you're not alone. Most fast-growing companies reach a point where cloud bills 
                    become unpredictable, cost accountability is unclear, and engineering teams are forced to choose between 
                    speed and efficiency.
                  </p>
                </div>
              </div>
            </div>
          </div>
  
          {/* Solution with enhanced visual design */}
          <div className="grid lg:grid-cols-2 gap-12 mb-12">
            {/* Left: Solution narrative */}
            <div className="bg-white rounded-3xl shadow-xl border p-10 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-24 h-24 bg-gradient-to-br from-blue-50 to-green-50 rounded-full transform -translate-x-12 -translate-y-12"></div>
              
              <div className="relative z-10">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-green-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">Our Solution</h2>
                  </div>
                </div>
                
                <p className="text-gray-600 text-lg leading-relaxed mb-6">
                  Our FinOps Strategy Services are designed to help you turn cloud spending from a liability into a lever for 
                  scale. We help you build a cloud cost operating model that gives your engineering and finance teams shared 
                  visibility, shared responsibility, and the power to act.
                </p>
                
                <p className="text-gray-600 leading-relaxed mb-8">
                  We start by understanding your cloud usage patterns, business goals, and technical architecture. From there, 
                  we design a cost optimization framework tailored to your environment — whether you're running on AWS, GCP, 
                  Azure, or Kubernetes.
                </p>
  
                <div className="inline-flex items-center gap-2 text-blue-600 font-semibold hover:gap-3 transition-all cursor-pointer">
                  <span>Learn more about our approach</span>
                  <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </div>
  
            {/* Right: Features with modern design */}
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-gray-900 mb-6">What You Get</h3>
              {features.map((feature, index) => (
                <div key={index} className="group bg-white rounded-2xl shadow-lg border p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-green-500 transition-colors">
                      <CheckCircle className="w-5 h-5 text-green-600 group-hover:text-white transition-colors" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="text-gray-400 group-hover:text-gray-600 transition-colors">
                          {feature.icon}
                        </div>
                      </div>
                      <span className="text-gray-700 leading-relaxed">{feature.text}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };  

  return (
    <div className="enhanced-service-content">
      {sections.map((section, index) => (
        <section key={index} className="content-section">
          <div className="section-header">
            <div className="section-number">{String(index + 1).padStart(2, '0')}</div>
            <h2 className="section-title">{section.title}</h2>
          </div>
          
          <div className="section-content">
            {section.type === 'cards' && section.content && renderCards(section.content)}
            {section.type === 'benefits' && section.content && renderBenefits(section.content)}
            {section.type === 'process' && section.processSteps && renderProcess(section.processSteps)}
            {section.type === 'tools' && section.tools && renderTools(section.tools)}
            {section.type === 'list' && section.items && renderList(section.items)}
            {section.type === 'rich-text' && renderRichText()}
            
            {/* Default to cards if no type specified */}
            {!section.type && section.content && renderCards(section.content)}
          </div>
        </section>
      ))}
    </div>
  );
};

export default ServiceContent; 